import React from 'react';

/**
 * /XOVR_Converter
 *
 * Storefront for the XOVR Converter Windows desktop app. Static page;
 * the actual conversion work happens in the downloaded app.
 */

const APP_VERSION = '1.0';
const SETUP_SIZE_MB = 40;

const SETUP_HREF = '/XOVR_Converter/Setup_XOVR_Converter.exe';

const FEATURES: Array<{ head: string; body: string }> = [
    {
        head: 'Two-row converter',
        body: 'Independent feet/inches ↔ mm rows with per-row lock and auto-collapse. Convert two values side-by-side without losing either.',
    },
    {
        head: 'AISC reference charts',
        body: 'Built-in lookups for American Standard Channels (C-shapes), I-Beams (S-shapes), and Wide Flange H-Beams (W-shapes). 118 sections, fully offline.',
    },
    {
        head: 'Tubing length tracker',
        body: 'Add cuts to a running list, edit and re-order entries, and total inches/feet across the job. Persists between sessions.',
    },
    {
        head: 'Tray-resident, no clutter',
        body: 'Collapses to a small tray button when not in use, expands on click. Remembers position across sessions.',
    },
];

const CHART_TYPES: Array<[string, string, string]> = [
    ['Channel', 'C-shapes', '26 sections — C3 × 4.1 through C15 × 50'],
    ['I-Beam', 'S-shapes', '31 sections — S3 × 5.7 through S24 × 121'],
    ['H-Beam', 'W-shapes', '61 sections — W4 × 13 through W24 × 84'],
];

export const XOVRConverterPage: React.FC = () => {
    return (
        <div style={s.page}>
            <div style={s.container}>
                {/* Top bar with home link */}
                <div style={s.topBar}>
                    <a href="/" style={s.backLink}>← XOVR/tools</a>
                </div>

                {/* Hero */}
                <header style={s.hero}>
                    <div style={s.eyebrow}>For tube laser shops</div>
                    <h1 style={s.title}>XOVR Converter</h1>
                    <p style={s.lede}>
                        Collapsible feet/inches ↔ millimeter widget with built-in AISC channel,
                        I-beam, and H-beam reference charts. Lives in your tray, expands when
                        you need it, gets out of the way when you don&rsquo;t.
                    </p>

                    <div style={s.ctaRow}>
                        <a href={SETUP_HREF} download style={s.primaryBtn}>
                            <span style={{ marginRight: 8 }}>↓</span>
                            Download for Windows ({SETUP_SIZE_MB} MB)
                        </a>
                    </div>

                    <div style={s.metaRow}>
                        v{APP_VERSION} &middot; Windows 10 / 11 &middot; Inno Setup installer &middot;
                        Adds Start Menu &amp; Desktop shortcuts
                    </div>
                </header>

                {/* Features */}
                <Section title="What it does">
                    <div style={s.grid4}>
                        {FEATURES.map((f) => (
                            <ValueCard key={f.head} head={f.head} body={f.body} />
                        ))}
                    </div>
                </Section>

                {/* Reference charts */}
                <Section
                    title="Reference charts"
                    subtitle="Three AISC steel section families baked in. Filter by section, depth, or weight; values include web thickness, flange thickness, k-zone, and tapered-flange start/end thickness for laser parameter lookup."
                >
                    <div style={s.tableWrap}>
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Chart</th>
                                    <th style={s.th}>AISC family</th>
                                    <th style={s.th}>Coverage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CHART_TYPES.map(([chart, family, coverage]) => (
                                    <tr key={chart}>
                                        <td style={{ ...s.td, color: '#fff', fontWeight: 600 }}>{chart}</td>
                                        <td style={{ ...s.td, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>{family}</td>
                                        <td style={s.td}>{coverage}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>

                {/* Install */}
                <Section title="Install" subtitle="Standard Windows installer, no admin rights required by default.">
                    <div style={s.grid3}>
                        <Step n={1} head="Download the installer">
                            Save{' '}
                            <a href={SETUP_HREF} style={s.inlineLink} download>Setup_XOVR_Converter.exe</a>{' '}
                            ({SETUP_SIZE_MB} MB) and double-click it.
                        </Step>
                        <Step n={2} head="Run through the wizard">
                            Windows SmartScreen may warn the first time &mdash; click{' '}
                            <em>More info</em> &rarr; <em>Run anyway</em>. The installer is unsigned;
                            code-signing will come once usage settles.
                        </Step>
                        <Step n={3} head="Launch from the tray">
                            Start Menu shortcut, optional Desktop shortcut, optional auto-start with Windows.
                            The widget collapses to a small tray button until you click it.
                        </Step>
                    </div>
                </Section>

                {/* FAQ */}
                <Section title="FAQ">
                    <div style={s.grid2}>
                        <Faq
                            q="Why does Windows SmartScreen warn me?"
                            a="The build is unsigned. Click 'More info' → 'Run anyway.' The installer doesn't talk to the network and only writes to your user profile."
                        />
                        <Faq
                            q="Does it need admin rights?"
                            a="No by default — the installer installs per-user. You can elevate to install for all users if you want a shared install."
                        />
                        <Faq
                            q="Where does it store its config?"
                            a="In your user profile: %USERPROFILE%\.feet_to_mm_config.json (window position) and similar dot-files for each chart's saved size and position."
                        />
                        <Faq
                            q="Does it talk to the internet?"
                            a="No. Pure offline calculator and reference. All AISC chart data is bundled into the executable."
                        />
                        <Faq
                            q="How do I uninstall?"
                            a="Settings → Apps → XOVR Converter → Uninstall, or run the uninstaller from the Start Menu group. The uninstaller removes the config file too."
                        />
                        <Faq
                            q="Can I use it without installing?"
                            a="Not yet. The MSI-style installer is the only distribution today — the bare exe lives at dist/XOVR_Converter.exe inside the build but isn't published separately."
                        />
                    </div>
                </Section>

                <footer style={s.footer}>
                    XOVR Parts &amp; Service &mdash; XOVR Converter v{APP_VERSION} &middot;
                    For tube laser cutting shops.
                </footer>
            </div>
        </div>
    );
};

/* ─────────────────── Sub-components ─────────────────── */

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
    <section style={s.section}>
        <h2 style={s.h2}>{title}</h2>
        {subtitle && <p style={s.subtitle} dangerouslySetInnerHTML={{ __html: subtitle }} />}
        <div style={{ marginTop: 24 }}>{children}</div>
    </section>
);

const ValueCard: React.FC<{ head: string; body: string }> = ({ head, body }) => (
    <div style={s.card}>
        <div style={s.accentBar} />
        <div style={s.cardHead}>{head}</div>
        <div style={s.cardBody}>{body}</div>
    </div>
);

const Step: React.FC<{ n: number; head: string; children: React.ReactNode }> = ({ n, head, children }) => (
    <div style={s.card}>
        <div style={s.stepRow}>
            <span style={s.stepBubble}>{n}</span>
            <span style={s.cardHead}>{head}</span>
        </div>
        <div style={{ ...s.cardBody, marginTop: 12 }}>{children}</div>
    </div>
);

const Faq: React.FC<{ q: string; a: string }> = ({ q, a }) => (
    <div style={s.card}>
        <div style={s.cardHead}>{q}</div>
        <div style={s.cardBody}>{a}</div>
    </div>
);

/* ─────────────────────── Styles ───────────────────────── */
const s: Record<string, React.CSSProperties> = {
    page: {
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at top, #1a1a1a 0%, #0a0a0a 100%)',
        color: '#e5e5e5',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '40px 20px 80px',
    },
    container: { maxWidth: 1080, margin: '0 auto' },
    topBar: { marginBottom: 24 },
    backLink: {
        color: '#888',
        textDecoration: 'none',
        fontSize: 13,
    },

    /* Hero */
    hero: {
        padding: '32px 0 56px',
        borderBottom: '1px solid #1f1f1f',
        textAlign: 'left',
    },
    eyebrow: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 999,
        border: '1px solid #2a2a2a',
        background: '#141414',
        color: '#bbb',
        fontSize: 12,
        marginBottom: 16,
    },
    title: {
        fontSize: 48,
        lineHeight: 1.1,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: '#ffffff',
        margin: 0,
    },
    lede: {
        marginTop: 16,
        color: '#bbb',
        fontSize: 17,
        lineHeight: 1.55,
        maxWidth: 720,
    },
    ctaRow: { marginTop: 28, display: 'flex', flexWrap: 'wrap', gap: 12 },
    primaryBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        background: '#ff4500',
        color: '#fff',
        textDecoration: 'none',
        padding: '12px 18px',
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 14,
        boxShadow: '0 6px 16px rgba(255, 69, 0, 0.25)',
    },
    metaRow: { marginTop: 12, color: '#777', fontSize: 12 },

    /* Sections */
    section: { padding: '56px 0', borderBottom: '1px solid #1f1f1f' },
    h2: { fontSize: 26, fontWeight: 700, letterSpacing: '-0.01em', color: '#fff', margin: 0 },
    subtitle: { marginTop: 8, color: '#999', fontSize: 15, maxWidth: 720 },

    /* Grids */
    grid4: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16,
    },
    grid3: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 20,
    },
    grid2: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 20,
    },

    /* Cards */
    card: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        padding: 20,
    },
    accentBar: {
        width: 28,
        height: 3,
        borderRadius: 999,
        background: '#ff4500',
        marginBottom: 12,
    },
    cardHead: { color: '#fff', fontWeight: 600, fontSize: 15 },
    cardBody: { color: '#999', fontSize: 13.5, lineHeight: 1.55, marginTop: 8 },

    /* Step */
    stepRow: { display: 'flex', alignItems: 'center', gap: 10 },
    stepBubble: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: 999,
        background: '#ff4500',
        color: '#fff',
        fontSize: 13,
        fontWeight: 700,
    },

    /* Table */
    tableWrap: {
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        overflow: 'hidden',
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: {
        textAlign: 'left',
        padding: '12px 16px',
        background: '#0f0f0f',
        color: '#888',
        fontSize: 11,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        borderBottom: '1px solid #2a2a2a',
        fontWeight: 600,
    },
    td: { padding: '12px 16px', color: '#ccc', borderBottom: '1px solid #1f1f1f' },

    /* Misc */
    inlineLink: { color: '#ff4500', textDecoration: 'none', borderBottom: '1px solid #ff4500' },
    footer: {
        marginTop: 56,
        paddingTop: 24,
        textAlign: 'center',
        color: '#555',
        fontSize: 12,
        borderTop: '1px solid #1f1f1f',
    },
};

export default XOVRConverterPage;
